// Import model
import { MultiReferenceFieldOptions } from '../models/MultiReferenceFieldOptions';

// Import base class serializer
import { ReferenceFieldOptionsSerializer } from './ReferenceFieldOptionsSerializer';

// Import dependency types (models and enums used in properties)
import { Condition } from '../models/Condition';
import { FieldOptions } from '../models/FieldOptions';
import { FormAction } from '../models/FormAction';
import { SelectFieldOptions } from '../models/SelectFieldOptions';

// Import dependency serializers (for complex types)
import { ConditionSerializer } from './ConditionSerializer';
import { FieldOptionsSerializer } from './FieldOptionsSerializer';
import { FormActionSerializer } from './FormActionSerializer';
import { SelectFieldOptionsSerializer } from './SelectFieldOptionsSerializer';

// Import derived type serializers (for polymorphic deserialization)

/**
 * Serializer for MultiReferenceFieldOptions - handles JSON serialization and deserialization with type validation
 */
export class MultiReferenceFieldOptionsSerializer {
  /**
   * Deserializes a JSON string into a MultiReferenceFieldOptions instance
   */
  static deserialize(json: string): MultiReferenceFieldOptions {
    const obj = JSON.parse(json);
    return this.fromObject(obj);
  }

  /**
   * Deserializes a plain object into a MultiReferenceFieldOptions instance
   */
  static fromObject(obj: any): MultiReferenceFieldOptions {
    if (!obj) {
      throw new Error('Cannot deserialize null or undefined to MultiReferenceFieldOptions');
    }


    const instance: MultiReferenceFieldOptions = {
      t: obj['_t'] as string,
      linkUrl: obj['LinkUrl'] as string | undefined,
      linkLabel: obj['LinkLabel'] as string | undefined,
      items: obj['Items'] as string | undefined,
      allowUnknown: obj['AllowUnknown'] as boolean | undefined,
      objectType: obj['ObjectType'] as string | undefined,
      objectTypeName: obj['ObjectType|Name'] as string | undefined,
      autoComplete: obj['AutoComplete'] as boolean | undefined,
      foreignFieldName: obj['ForeignFieldName'] as string | undefined,
      criteria: obj['Criteria'] ? (obj['Criteria'] as any[]).map((item: any) =>
        ConditionSerializer.fromObject(item)
      ) : [] as any,
      joinBehavior: obj['JoinBehavior'] as string | undefined,
      actions: obj['Actions'] ? (obj['Actions'] as any[]).map((item: any) =>
        FormActionSerializer.fromObject(item)
      ) : [] as any,
      contributeUserEvents: obj['ContributeUserEvents'] as string | undefined,
      valueType: obj['ValueType'] as string | undefined,
      f2656103: obj['_f2656103'] as any | undefined,
    };

    return instance;
  }

  /**
   * Deserializes a JSON string into an array of MultiReferenceFieldOptions instances
   */
  static deserializeArray(json: string): MultiReferenceFieldOptions[] {
    const arr = JSON.parse(json);
    if (!Array.isArray(arr)) {
      throw new Error('Expected JSON array');
    }
    return arr.map(obj => this.fromObject(obj));
  }

  /**
   * Serializes a MultiReferenceFieldOptions instance to a JSON string
   */
  static serialize(instance: MultiReferenceFieldOptions): string {
    return JSON.stringify(this.toObject(instance));
  }

  /**
   * Converts a MultiReferenceFieldOptions instance to a plain object
   */
  static toObject(instance: MultiReferenceFieldOptions): any {
    return {
      '_t': instance.t,
      'LinkUrl': instance.linkUrl,
      'LinkLabel': instance.linkLabel,
      'Items': instance.items,
      'AllowUnknown': instance.allowUnknown,
      'ObjectType': instance.objectType,
      'ObjectType|Name': instance.objectTypeName,
      'AutoComplete': instance.autoComplete,
      'ForeignFieldName': instance.foreignFieldName,
      'Criteria': instance.criteria?.map((item: any) =>
        ConditionSerializer.toObject(item)
      ),
      'JoinBehavior': instance.joinBehavior,
      'Actions': instance.actions?.map((item: any) =>
        FormActionSerializer.toObject(item)
      ),
      'ContributeUserEvents': instance.contributeUserEvents,
      'ValueType': instance.valueType,
      '_f2656103': instance.f2656103,
    };
  }

  /**
   * Serializes an array of MultiReferenceFieldOptions instances to a JSON string
   */
  static serializeArray(instances: MultiReferenceFieldOptions[]): string {
    return JSON.stringify(instances.map(inst => this.toObject(inst)));
  }
}
